import { postStateOf, readPostPlan, readPublishedPost } from './TaskPostState';
export const CONTENT_KINDS = ['social', 'email', 'blog'];
export const CONTENT_STATES = ['idea', 'drafting', 'working', 'ready', 'scheduled', 'done', 'dropped', 'failed'];
export const PREPARE_NOW_WINDOW_MS = 15 * 60_000;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_TEXT = 1000;
const MAX_ID = 200;
const MAX_URL = 2048;
function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : undefined;
}
const text = value => typeof value === 'string' && value.trim() ? value.trim() : undefined;
function capped(value, max) {
  const raw = text(value);
  return raw ? Array.from(raw).slice(0, max).join('') : undefined;
}
function guid(value) {
  const raw = text(value);
  return raw && GUID.test(raw) ? raw : undefined;
}
function instant(value) {
  const at = text(value);
  return at && Number.isFinite(Date.parse(at)) ? at : undefined;
}
function httpsUrl(value) {
  const raw = text(value);
  if (!raw || raw.length > MAX_URL) {
    return undefined;
  }
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && !!url.hostname && !url.username && !url.password ? raw : undefined;
  } catch {
    return undefined;
  }
}
export function readEmailPreviewCard(display) {
  const card = asObject(display == null ? void 0 : display.emailPreviewCard);
  const draftId = guid(card == null ? void 0 : card.draftId);
  if (!card || !draftId) {
    return null;
  }
  const preheader = capped(card.preheader, MAX_TEXT);
  const excerpt = capped(card.excerpt, MAX_TEXT);
  const updatedAt = instant(card.updatedAt);
  return {
    draftId,
    title: capped(card.title, MAX_TEXT) ?? '',
    subject: capped(card.subject, MAX_TEXT) ?? '',
    ...(preheader ? {
      preheader
    } : {}),
    ...(excerpt ? {
      excerpt
    } : {}),
    ...(updatedAt ? {
      updatedAt
    } : {})
  };
}
export function readBlogPostPreviewCard(display) {
  const card = asObject(display == null ? void 0 : display.blogPostPreviewCard);
  const draftId = guid(card == null ? void 0 : card.draftId);
  if (!card || !draftId) {
    return null;
  }
  const excerpt = capped(card.excerpt, MAX_TEXT);
  const updatedAt = instant(card.updatedAt);
  const preview = asObject(card.wixPreview);
  const siteId = guid(preview == null ? void 0 : preview.siteId);
  const draftPostId = guid(preview == null ? void 0 : preview.draftPostId);
  return {
    draftId,
    title: capped(card.title, MAX_TEXT) ?? '',
    ...(excerpt ? {
      excerpt
    } : {}),
    ...(updatedAt ? {
      updatedAt
    } : {}),
    ...(siteId && draftPostId ? {
      wixPreview: {
        siteId,
        draftPostId
      }
    } : {})
  };
}
function readDelivery(value, doneKey) {
  const delivery = asObject(value);
  if (!delivery) {
    return null;
  }
  const doneAt = instant(delivery[doneKey]);
  const scheduledAt = instant(delivery.scheduledAt);
  if (!doneAt && !scheduledAt) {
    return null;
  }
  const channel = capped(delivery.channel, 40);
  const url = httpsUrl(delivery.url);
  const externalIdText = text(delivery.externalId);
  const externalId = externalIdText && externalIdText.length <= MAX_ID ? externalIdText : undefined;
  return {
    ...(channel ? {
      channel
    } : {}),
    ...(doneAt ? {
      [doneKey]: doneAt
    } : {}),
    ...(scheduledAt ? {
      scheduledAt
    } : {}),
    ...(url ? {
      url
    } : {}),
    ...(externalId ? {
      externalId
    } : {})
  };
}
export function readSentEmail(display) {
  return readDelivery(display == null ? void 0 : display.sentEmail, 'sentAt');
}
export function readPublishedBlogPost(display) {
  return readDelivery(display == null ? void 0 : display.publishedBlogPost, 'publishedAt');
}
export function readContentDelivery(display) {
  const delivery = asObject(display == null ? void 0 : display.delivery);
  const publishAt = instant(delivery == null ? void 0 : delivery.publishAt);
  const publishedAt = instant(delivery == null ? void 0 : delivery.publishedAt);
  if (!publishAt && !publishedAt) {
    return null;
  }
  const url = httpsUrl(delivery == null ? void 0 : delivery.url);
  return {
    ...(publishAt ? {
      publishAt
    } : {}),
    ...(publishedAt ? {
      publishedAt
    } : {}),
    ...(url ? {
      url
    } : {})
  };
}
export function readSavedActionId(display) {
  const id = text(display == null ? void 0 : display.savedActionId);
  return id && id.length <= MAX_ID ? id : undefined;
}
export function readMarketingPlanPostAt(display) {
  var _asObject;
  return instant((_asObject = asObject(display == null ? void 0 : display.marketingPlanPost)) == null ? void 0 : _asObject.postAt);
}
export function readReplacedBy(display) {
  const id = text(display == null ? void 0 : display.replacedBy);
  return id && id.length <= MAX_ID ? id : undefined;
}
export function contentViewTaskId(task) {
  return readReplacedBy(asObject(task == null ? void 0 : task.display)) ?? (text(task == null ? void 0 : task.id) || undefined);
}
export function readPrepareNowAt(display) {
  return instant(display == null ? void 0 : display.prepareNowAt);
}
export function contentKindOf(display) {
  const plan = readPostPlan(display);
  if (plan) {
    return plan.network === 'email' ? 'email' : plan.network === 'blog' ? 'blog' : 'social';
  }
  if (readEmailPreviewCard(display)) {
    return 'email';
  }
  if (readBlogPostPreviewCard(display)) {
    return 'blog';
  }
  if (asObject(display == null ? void 0 : display.socialPostPreviewCard) || asObject(display == null ? void 0 : display.publishedPost) || asObject(display == null ? void 0 : display.marketingPlanPost)) {
    return 'social';
  }
  if (readSentEmail(display)) {
    return 'email';
  }
  if (readPublishedBlogPost(display)) {
    return 'blog';
  }
  return readContentDelivery(display) ? 'social' : undefined;
}
export function draftCardOf(display, kind) {
  return kind === 'email' ? readEmailPreviewCard(display) : kind === 'blog' ? readBlogPostPreviewCard(display) : null;
}
export function hasContentCard(display, kind) {
  const social = asObject(display == null ? void 0 : display.socialPostPreviewCard);
  return kind === 'social' ? !!(text(social == null ? void 0 : social.decisionId) || text(social == null ? void 0 : social.draftId)) : !!draftCardOf(display, kind);
}
export function contentPublishedOf(display) {
  const delivery = readContentDelivery(display);
  if (delivery != null && delivery.publishedAt) {
    return {
      at: delivery.publishedAt,
      ...(delivery.url ? {
        url: delivery.url
      } : {})
    };
  }
  const post = readPublishedPost(display);
  if (post && !post.scheduledAt) {
    return {
      at: post.publishedAt,
      ...(post.url ? {
        url: post.url
      } : {})
    };
  }
  const sent = readSentEmail(display);
  if (sent != null && sent.sentAt) {
    return {
      at: sent.sentAt,
      ...(sent.url ? {
        url: sent.url
      } : {})
    };
  }
  const blog = readPublishedBlogPost(display);
  if (blog != null && blog.publishedAt) {
    return {
      at: blog.publishedAt,
      ...(blog.url ? {
        url: blog.url
      } : {})
    };
  }
  return undefined;
}
export function contentHeldAt(display) {
  const post = readPublishedPost(display);
  if (post != null && post.scheduledAt) {
    return post.scheduledAt;
  }
  const sent = readSentEmail(display);
  if (sent != null && sent.scheduledAt && !sent.sentAt) {
    return sent.scheduledAt;
  }
  const blog = readPublishedBlogPost(display);
  return blog != null && blog.scheduledAt && !blog.publishedAt ? blog.scheduledAt : undefined;
}
export function contentPublishAt(display) {
  var _readContentDelivery;
  return (_readContentDelivery = readContentDelivery(display)) == null ? void 0 : _readContentDelivery.publishAt;
}
export function contentGoOutAt(display) {
  return contentPublishAt(display) ?? readMarketingPlanPostAt(display);
}
export function contentPlacementAt(display) {
  const delivery = readContentDelivery(display);
  if (delivery != null && delivery.publishedAt) {
    return delivery.publishedAt;
  }
  if (delivery != null && delivery.publishAt) {
    return delivery.publishAt;
  }
  const post = readPublishedPost(display);
  if (post) {
    return post.scheduledAt ?? post.publishedAt;
  }
  return readMarketingPlanPostAt(display);
}
export function isSavedActionScheduled(task) {
  if (!task || task.status !== 'PENDING' || !readSavedActionId(asObject(task.display))) {
    return false;
  }
  return !(task.blockedByDecisions ?? []).some(edge => !!text(edge == null ? void 0 : edge.decisionId) && !text(edge == null ? void 0 : edge.resolvedChoice));
}
function preparedRecently(display, now) {
  const at = Date.parse(readPrepareNowAt(display) ?? '');
  return Number.isFinite(at) && now - at <= PREPARE_NOW_WINDOW_MS && at - now <= PREPARE_NOW_WINDOW_MS;
}
export function contentStateOf(task, now) {
  if (now === void 0) {
    now = Date.now();
  }
  if (!task) {
    return undefined;
  }
  const display = asObject(task.display);
  const kind = contentKindOf(display);
  if (!kind) {
    return undefined;
  }
  if (contentPublishedOf(display)) {
    return 'done';
  }
  if (isSavedActionScheduled(task) || contentHeldAt(display)) {
    return 'scheduled';
  }
  const status = task.status ?? undefined;
  if (status === 'CANCELLED' || status === 'MOOT') {
    return 'dropped';
  }
  if (status === 'FAILED') {
    return 'failed';
  }
  const card = hasContentCard(display, kind);
  if (card && status === 'IN_PROGRESS') {
    return 'working';
  }
  if (kind === 'social' ? card && postStateOf(task) === 'needsApproval' : card) {
    return 'ready';
  }
  if (card) {
    return undefined;
  }
  if (status === 'IN_PROGRESS' || status === 'PENDING' && preparedRecently(display, now)) {
    return 'drafting';
  }
  return status === 'PENDING' && (readPostPlan(display) || contentPublishAt(display)) ? 'idea' : undefined;
}
export function contentNotDrawn(task, now) {
  if (now === void 0) {
    now = Date.now();
  }
  const display = asObject(task == null ? void 0 : task.display);
  const kind = contentKindOf(display);
  if (!task || !kind) {
    return false;
  }
  if (readReplacedBy(display)) {
    return true;
  }
  const state = contentStateOf(task, now);
  if (state === 'dropped' || state === 'failed') {
    return true;
  }
  return task.status === 'COMPLETED' && !hasContentCard(display, kind) && !contentPublishedOf(display) && !contentHeldAt(display);
}